import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const checkStrength = (pw) => ({
  length: pw.length >= 12,
  upper: /[A-Z]/.test(pw),
  lower: /[a-z]/.test(pw),
  digit: /\d/.test(pw),
});

export default function ChangePasswordPage() {
  useDocumentTitle("Change password", "Update your CRM account password.");
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const mustChange = !!user?.must_change_password;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const strength = useMemo(() => checkStrength(next), [next]);
  const allMet = Object.values(strength).every(Boolean);
  const match = confirm.length > 0 && next === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allMet) return setError("New password doesn't meet the requirements.");
    if (!match) return setError("New passwords don't match.");
    if (current === next) return setError("New password must be different from your current password.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed successfully");
      if (refresh) await refresh();
      navigate("/");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 400 && /current/i.test(detail)) setError("Current password is incorrect.");
      else setError(detail || "Couldn't change password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="change-password-page">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight">
          {mustChange ? "Set a new password" : "Change password"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {mustChange
            ? "For security, please choose a new password before continuing."
            : "Update the password on your account."}
        </p>
      </div>

      <div className="bg-white border border-border rounded-md p-5">
        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="change-error">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs font-medium">Current password</Label>
            <Input
              type={showPass ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className="mt-1"
              data-testid="change-current-input"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">New password</Label>
            <div className="relative mt-1">
              <Input
                type={showPass ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                data-testid="change-new-input"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Confirm new password</Label>
            <Input
              type={showPass ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="mt-1"
              data-testid="change-confirm-input"
            />
            {confirm && !match && <p className="text-xs text-red-600 mt-1">Passwords don't match</p>}
          </div>

          <ul className="text-xs space-y-1 bg-slate-50 border border-border rounded-md p-3">
            <li className={strength.length ? "text-emerald-700" : "text-muted-foreground"}>{strength.length ? "✓" : "○"} At least 12 characters</li>
            <li className={strength.upper ? "text-emerald-700" : "text-muted-foreground"}>{strength.upper ? "✓" : "○"} One uppercase letter</li>
            <li className={strength.lower ? "text-emerald-700" : "text-muted-foreground"}>{strength.lower ? "✓" : "○"} One lowercase letter</li>
            <li className={strength.digit ? "text-emerald-700" : "text-muted-foreground"}>{strength.digit ? "✓" : "○"} One number</li>
          </ul>

          <div className="flex items-center gap-2 justify-end">
            {!mustChange && (
              <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="change-cancel-btn">
                Cancel
              </Button>
            )}
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white gap-1" disabled={loading || !allMet || !match} data-testid="change-submit-btn">
              <KeyRound size={14} />
              {loading ? "Updating…" : "Update password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
